<template>
  <MyForm :title="'Postech Notes'">
    <template v-slot:fields>
      <!-- start postech notes -->
      <FormSelect
        :name="'select pile:'"
        :options="allPiles"
        :displayOptions="pileDisplayOptions"
        v-model:selected="selectedPile"
      ></FormSelect>
      <!--p class="debug">selected= {{ selectedPile }}</p-->

      <FormInput
        :name="'pile count:'"
        v-model="pileCount"
        :placeholder="'enter number of piles'"
      ></FormInput>
      <FormInput
        :name="'install torque:'"
        v-model="installTorque"
        :placeholder="'min 1000 ft-lbs typical'"
      ></FormInput>
      <FormInput
        :name="'SLS Capacity:'"
        v-model="slsCapacity"
        :readonly="true"
        :disabled="true"
      ></FormInput>
      <FormInput
        :name="'ULS Capacity:'"
        v-model="ulsCapacity"
        :readonly="true"
        :disabled="true"
      ></FormInput>

      <div>
        <FormTextArea :name="'Pile Notes'" :rows="7" :cols="40" v-model="pileNotes"></FormTextArea>
        <FormBtn :name="'copy'" @clicked="copyPileNotes"></FormBtn>
      </div>

      <div>
        <FormTextArea
          :name="'Simple Notes'"
          :rows="4"
          :cols="40"
          v-model="simpleNotes"
        ></FormTextArea>
        <FormBtn :name="'copy'" @clicked="copySimpleNotes"></FormBtn>
      </div>
    </template>
  </MyForm>

  <MyForm :title="'Engineered Notes'">
    <template v-slot:fields>
      <FormInput
        :name="'sheet no:'"
        v-model="sheetNo"
        :placeholder="'enter eng drawing sheet no.'"
      ></FormInput>
      <FormInput :name="'title:'" v-model="title" :placeholder="'drawing title'"></FormInput>
      <FormInput :name="'company:'" v-model="company"></FormInput>
      <FormInput
        :name="'sealed by:'"
        v-model="sealedBy"
        :placeholder="'stamping engineers name'"
      ></FormInput>
      <FormInput :name="'date:'" v-model="date" :placeholder="'stamped date'"></FormInput>
      <div>
        <FormTextArea
          :name="'Engineered Notes'"
          :rows="9"
          :cols="40"
          v-model="engNotes"
        ></FormTextArea>
        <FormBtn :name="'copy'" @clicked="copyEngNotes"></FormBtn>
      </div>
    </template>
  </MyForm>

  <p class="debug">prop location : {{ props.location }}</p>
  <p class="debug">default pile : {{ defaultPile }}</p>

</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { FormSelect, FormInput, FormTextArea, FormBtn, MyForm } from '@/components/form'
import { usePostechStore, usePileStore } from '@/stores'
import { storeToRefs } from 'pinia';
const postechStore = usePostechStore()
const pileStore = usePileStore()
//const allPileOptions = await postechStore.getAllPileTypes()
const { allPiles } = storeToRefs(pileStore)
const pileDisplayOptions = allPiles.value.map((piletype) => {
  return piletype.name
})

//const props = defineProps({ location : { type : String}});
const props = defineProps<{
  location: string
}>()

//default to Postech P312L712
const defaultPile = computed(()=>{
  return allPiles.value.find(pile =>{
    return pile.id == 50;
  })
})

const selectedPile = ref(defaultPile.value)
const pileCount = ref<Number>(1)
const installTorque = ref<Number>(1000)
const slsCapacity = computed(() => {
  let d = selectedPile.value['od_in']
  let d_eff = Math.pow(d, 0.92)
  let lam_k = 22 //1415.32;
  let kt = lam_k / d_eff
  let p_ult = (kt * installTorque.value) / 2.5
  return Math.round(p_ult / 100) * 100
})
const ulsCapacity = computed(() => {
  let p = (slsCapacity.value * 2.5) / 2
  return Math.round(p / 100) * 100
})

const pileNotes = computed(() => {
  return `${selectedPile.value.description} MIN (${pileCount.value} TOTAL)
MIN INSTALL TORQUE : ${installTorque.value} ft-lbs U.N.O. (SEE PILES)
MIN PILE DESIGN LOAD CAPACITY: ${slsCapacity.value} LBS (SLS) , ${ulsCapacity.value} LBS (ULS)  U.N.O. (SEE PILES)
INSTALL HELIX TO BELOW LOCAL FROST DEPTH AND AT LEAST 5' BELOW GRADE
TOP OF PILE SHALL BE MAX 12" ABOVE FINISHED GRADE
PILE CAPS SHALL BE TYPE AS ADJUSTABLE CAPS WITH TAU U-BRACKETS BY POSTECH OR APPROVED EQUAL
DO NOT EXCEED MAX INSTALL TORQUE OF ${selectedPile.value.max_torque} ft-lbs`
})

const simpleNotes = computed(() => {
  return `${installTorque.value} ft-lbs
${slsCapacity.value} lbs SLS
${ulsCapacity.value} lbs ULS`
})

/**
 * utility function to copy the textarea content to clipboard on button click
 *
 */
function copyPileNotes() {
  console.log('postech notes simple copyPileNotes()...')

  let copyText = pileNotes.value
  // Get the text field
  //var copyText = document.getElementById("projectName").innerText;
  //let copyText = element.value;
  // Copy the text inside the text field
  navigator.clipboard.writeText(copyText)
  // Alert the copied text
  //alert("Copied the text: " + copyText);
  console.log(copyText + ' copied to clipboard')
  //toast(copyText + " copied to clipboard");
}

/**
 * utility function to copy the textarea content to clipboard on button click
 *
 */
function copySimpleNotes() {
  console.log('postech notes simple copySimpleNotes()...')

  let copyText = simpleNotes.value
  // Get the text field
  //var copyText = document.getElementById("projectName").innerText;
  //let copyText = element.value;
  // Copy the text inside the text field
  navigator.clipboard.writeText(copyText)
  // Alert the copied text
  //alert("Copied the text: " + copyText);
  console.log(copyText + ' copied to clipboard')
  //toast(copyText + " copied to clipboard");
}

//engineered drawings notes:
const sheetNo = ref('')
const title = ref('')
const company = ref('')
const sealedBy = ref('')
const date = ref('')

const engNotes = computed(() => {
  return `Location: ${props.location}
All pile locations and required loading as per project drawings sheet ${sheetNo.value} '${title.value}' by ${company.value}, sealed by ${sealedBy.value} on ${date.value}. (ref:attached)
Pile Count: ${pileCount.value}
MIN INSTALL TORQUE : ${installTorque.value} ft-lbs U.N.O. (SEE PILES), MAX INSTALL TORQUE : ${selectedPile.value.max_torque} ft-lbs
MIN PILE DESIGN LOAD CAPACITY: ${slsCapacity.value} LBS (SLS) , ${ulsCapacity.value} LBS (ULS)  U.N.O. (SEE PILES)
INSTALL HELIX TO BELOW LOCAL FROST DEPTH AND AT LEAST 5' BELOW GRADE
TOP OF PILE SHALL BE MAX 12" ABOVE FINISHED GRADE
PILE CAPS SHALL BE TYPE AS ADJUSTABLE CAPS WITH TAU U-BRACKETS BY POSTECH OR APPROVED EQUAL`
})

function copyEngNotes() {
  console.log('postech notes simple copySimpleNotes()...')
  let copyText = engNotes.value
  navigator.clipboard.writeText(copyText)
  console.log(copyText + ' copied to clipboard')
  //toast(copyText + " copied to clipboard")
}
</script>
